import merge from "lodash/merge";
import { JSX, PropsWithChildren, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { createStore } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { migrateWorkflowStorage, MIGRATION_KEY } from "./helpers/migrate_workflow_store";
import { isValidPlannerOrder } from "./helpers/planner_order";
import { usePlannerDefaultOrder } from "./hooks/use_planner_default_order";
import { WorkflowStoreContext } from "./workflow_store_context";
import { WorkflowStoreType, WorkflowTab } from "./workflow_store_types";

const PERSIST_STORAGE_KEY = "workflow-store";

const DEFAULT_ENGAGEMENTS_STATE = {
  columns: [],
  resolvedColumns: [],
  order: {},
  currentPage: 1,
  groupBy: null,
  pinnedIds: [],
  selectedIds: {},
  modals: {
    share: false,
    bulkEdit: false,
    smartAllocation: false,
    rollForward: false
  }
};

const DEFAULT_ROLES_STATE = {
  columns: [],
  resolvedColumns: [],
  order: {},
  currentPage: 1,
  groupBy: null,
  pinnedIds: [],
  selectedIds: {},
  ganttView: false,
  modals: {
    share: false,
    bulkEdit: false,
    smartAllocation: false,
    closeRoles: false
  }
};

const DEFAULT_PLANNER_STATE = {
  selectedOrder: {},
  currentPage: 1
};

type WorkflowStoreProviderProps = PropsWithChildren<{
  initialTab?: WorkflowTab;
}>;

export const WorkflowStoreProvider = ({ children, initialTab }: WorkflowStoreProviderProps): JSX.Element => {
  const plannerDefaultOrder = usePlannerDefaultOrder();
  const navigate = useNavigate();

  const [store] = useState(() =>
    createStore<WorkflowStoreType>()(
      persist(
        immer((set) => ({
          tab: initialTab || WorkflowTab.Roles,
          permittedTabs: [],
          engagements: DEFAULT_ENGAGEMENTS_STATE,
          roles: DEFAULT_ROLES_STATE,
          planner: { ...DEFAULT_PLANNER_STATE, selectedOrder: plannerDefaultOrder },
          actions: {
            setTab: (tab: WorkflowTab) => {
              set((draft) => {
                draft.tab = tab;
                draft.roles.currentPage = 1;
                draft.engagements.currentPage = 1;
              });
              navigate(`/workflow/${tab}`);
            },
            setPermittedTabs: (tabs) =>
              set((draft) => {
                draft.permittedTabs = tabs;
              }),
            engagements: {
              setColumns: (columns) =>
                set((draft) => {
                  draft.engagements.columns = columns.map(({ name }) => name);
                  draft.engagements.resolvedColumns = columns;
                }),
              setOrder: (order) =>
                set((draft) => {
                  draft.engagements.order = order;
                  draft.engagements.currentPage = 1;
                }),
              setCurrentPage: (page) =>
                set((draft) => {
                  draft.engagements.currentPage = page;
                }),
              setGroupBy: (groupBy) =>
                set((draft) => {
                  draft.engagements.groupBy = groupBy;
                }),
              setPinnedIds: (ids) =>
                set((draft) => {
                  draft.engagements.pinnedIds = ids;
                }),
              setSelectedIds: (ids) =>
                set((draft) => {
                  draft.engagements.selectedIds =
                    typeof ids === "function" ? ids(draft.engagements.selectedIds) : ids;
                }),
              setModalVisibility: (modal, visible) =>
                set((draft) => {
                  draft.engagements.modals[modal] = visible;
                })
            },
            roles: {
              setColumns: (columns) =>
                set((draft) => {
                  draft.roles.columns = columns.map(({ name }) => name);
                  draft.roles.resolvedColumns = columns;
                }),
              setOrder: (order) =>
                set((draft) => {
                  draft.roles.order = order;
                  draft.roles.currentPage = 1;
                }),
              setCurrentPage: (page) =>
                set((draft) => {
                  draft.roles.currentPage = page;
                }),
              setGroupBy: (groupBy) =>
                set((draft) => {
                  draft.roles.groupBy = groupBy;
                }),
              setPinnedIds: (ids) =>
                set((draft) => {
                  draft.roles.pinnedIds = ids;
                }),
              setSelectedIds: (ids) =>
                set((draft) => {
                  draft.roles.selectedIds = typeof ids === "function" ? ids(draft.roles.selectedIds) : ids;
                }),
              setGanttView: (ganttView) =>
                set((draft) => {
                  draft.roles.ganttView = ganttView;
                }),
              setModalVisibility: (modal, visible) =>
                set((draft) => {
                  draft.roles.modals[modal] = visible;
                })
            },
            planner: {
              setSelectedOrder: (order) =>
                set((draft) => {
                  draft.planner.selectedOrder = isValidPlannerOrder(order) ? order : plannerDefaultOrder;
                  draft.planner.currentPage = 1;
                }),
              setCurrentPage: (page) =>
                set((draft) => {
                  draft.planner.currentPage = page;
                })
            }
          }
        })),
        {
          name: PERSIST_STORAGE_KEY,
          merge: (persisted, current) => {
            const merged = merge({}, current, persisted) as WorkflowStoreType;

            if (!isValidPlannerOrder(merged.planner.selectedOrder)) {
              merged.planner.selectedOrder = current.planner.selectedOrder;
            }

            return merged;
          },
          partialize: (state) => ({
            tab: state.tab,
            engagements: {
              columns: state.engagements.columns,
              order: state.engagements.order,
              pinnedIds: state.engagements.pinnedIds,
              groupBy: state.engagements.groupBy
            },
            roles: {
              columns: state.roles.columns,
              order: state.roles.order,
              ganttView: state.roles.ganttView,
              pinnedIds: state.roles.pinnedIds,
              groupBy: state.roles.groupBy
            },
            planner: {
              selectedOrder: state.planner.selectedOrder
            }
          })
        }
      )
    )
  );

  // TODO: [SP-8824] Remove this migration 30 days after release to production
  useEffect(() => {
    if (!window.localStorage.getItem(MIGRATION_KEY)) {
      store.setState(migrateWorkflowStorage(store.getState()));
    }
  }, []);

  return <WorkflowStoreContext.Provider value={store}>{children}</WorkflowStoreContext.Provider>;
};
